import { Injectable } from '@angular/core';

/**
 * Service for transforming cotizaciones and servicios data structures
 * Handles conversion between Firebase data and view models
 *
 * Category: Helper Service
 * Purpose: Data transformation, service array extraction, sorting
 */
@Injectable()
export class CotizacionesDataTransformerService {

    constructor() { }

    /**
     * Transforms Firebase cotizacion snapshot to view model with services array
     * @param cotizacion Firebase snapshot payload
     * @returns Transformed cotizacion with arrServicios property
     */
    transformCotizacionWithServiciosArray(cotizacion: any): any {
        const $key = cotizacion.payload.key;
        const data = { $key, ...cotizacion.payload.val() };

        const servicios_arr = [];
        for (const idServicio in data.servicios) {
            if (data.servicios.hasOwnProperty(idServicio)) {
                servicios_arr.push(data.servicios[idServicio]);
            }
        }

        data.arrServicios = servicios_arr;
        return data;
    }

    /**
     * Transforms array of Firebase cotizaciones to view models
     * @param cotizaciones Array of Firebase snapshots
     * @returns Array of transformed cotizaciones
     */
    transformCotizacionesArray(cotizaciones: any[]): any[] {
        const arregloCotizaciones = [];
        cotizaciones.forEach(cotizacion => {
            const transformed = this.transformCotizacionWithServiciosArray(cotizacion);
            arregloCotizaciones.push(transformed);
        });
        return arregloCotizaciones;
    }

    /**
     * Transforms Firebase clientes snapshot to sorted array
     * @param clientes Array of Firebase snapshots
     * @returns Sorted array of clientes by name
     */
    transformAndSortClientes(clientes: any[]): any[] {
        const clientesArr = [];
        clientes.forEach(cliente => {
            const $key = cliente.payload.key;
            const data = { $key, ...cliente.payload.val() };
            clientesArr.push(data);
        });

        clientesArr.sort(function(a, b) {
            if (a.nombre === undefined) {
                return 1;
            }
            else if (b.nombre === undefined) {
                return -1;
            }
            return a.nombre.toLowerCase().localeCompare(b.nombre.toLowerCase());
        });

        return clientesArr;
    }

    /**
     * Transforms Firebase confirmaciones to view model
     * @param confirmaciones Array of Firebase Firestore snapshots
     * @returns Array of transformed confirmaciones
     */
    transformConfirmacionesArray(confirmaciones: any[]): any[] {
        const arregloConfirmaciones = [];
        confirmaciones.forEach(confirmacion => {
            const $key = confirmacion.payload.doc.id;
            const data = { $key, ...confirmacion.payload.doc.data() };
            arregloConfirmaciones.push(data);
        });
        return arregloConfirmaciones;
    }

    /**
     * Extracts service data from cotizacion for Excel export
     * @param cotizacion Cotizacion data with servicios
     * @returns Cotizacion with extracted service fields
     */
    extractServicioDataForExport(cotizacion: any): any {
        const data = { ...cotizacion };

        if (data.servicios) {
            for (const idServicio in data.servicios) {
                if (data.servicios.hasOwnProperty(idServicio)) {
                    data.id_servicio = idServicio;

                    if (data.servicios[idServicio].itinerario) {
                        data.inicioDeServicio = data.servicios[idServicio].itinerario.inicioDeServicio;
                        data.finDeServicio = data.servicios[idServicio].itinerario.finDeServicio;
                    }

                    for (const idCombinacion in data.servicios[idServicio].precioCombinaciones) {
                        if (data.servicios[idServicio].precioCombinaciones.hasOwnProperty(idCombinacion)) {
                            data.monto = data.servicios[idServicio].precioCombinaciones[idCombinacion].total;
                        }
                    }

                    if (data.servicios[idServicio].origen) {
                        data.alias_origen = data.servicios[idServicio].origen.alias;
                    }
                    if (data.servicios[idServicio].destino) {
                        data.alias_destino = data.servicios[idServicio].destino.alias;
                    }
                }
            }
        }

        return data;
    }

    /**
     * Creates compressed cotizacion object for Excel export
     * @param cotizacion Full cotizacion data
     * @param key Cotizacion Firebase key
     * @returns Compressed cotizacion object with shortened property names
     */
    createCompressedCotizacion(cotizacion: any, key: string): any {
        return {
            k: key,
            i: cotizacion.inicioDeServicio,
            iT: cotizacion.idTrosten,
            o: cotizacion.alias_origen,
            d: cotizacion.alias_destino,
            c: cotizacion.cliente,
            fc: cotizacion.fechaDeCreacion,
            f: cotizacion.finDeServicio,
            co: cotizacion.confirmacion,
            u: cotizacion.usuarioQueCotizo,
            sta: cotizacion.estatus,
            mon: cotizacion.monto
        };
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/cotizaciones-data-transformer.service.ts